import React, { useState, useEffect } from 'react';
import { X, Save, Search } from 'lucide-react';
import { FaHandHoldingMedical } from "react-icons/fa6";
import { GiHorseHead } from 'react-icons/gi';
import { PiFarm } from 'react-icons/pi';
import { addDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

// Modal simple sans shadcn pour éviter les problèmes de z-index
const SimpleModal = ({ isOpen, onClose, children, title }) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: 10000 }}
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
            type="button"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4">
          {children}
        </div>
      </div>
    </div>
  );
};

const MedicalTicketModal = ({ 
  isOpen, 
  onClose, 
  horse = null, // Si fourni, on pré-remplit. Sinon, on affiche la recherche
  onSuccess 
}) => {
  // États du formulaire
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('moyenne');
  const [subType, setSubType] = useState('soins');
  const [saving, setSaving] = useState(false);
  
  // États pour la recherche d'animal (quand horse n'est pas fourni)
  const [horseName, setHorseName] = useState('');
  const [horseSire, setHorseSire] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredHorses, setFilteredHorses] = useState([]);
  const [allHorses, setAllHorses] = useState([]);
  const [selectedAnimalType, setSelectedAnimalType] = useState('equide');
  const [selectedHorseId, setSelectedHorseId] = useState(null);

  // Charger la liste des animaux si pas de cheval pré-sélectionné
  useEffect(() => {
    if (!horse && isOpen) {
      loadAnimaux();
    }
  }, [horse, isOpen]);

  // Réinitialiser à l'ouverture
  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setDescription('');
      setPriority('moyenne');
      setSubType('soins');
      
      if (horse) {
        // Si un cheval est fourni, on pré-remplit
        setHorseName(horse.nom || '');
        setHorseSire(horse.sire || '');
        setSelectedAnimalType(horse.type || 'equide');
        setSelectedHorseId(horse.id);
      } else {
        // Sinon, on réinitialise la recherche
        setHorseName('');
        setHorseSire('');
        setSelectedAnimalType('equide');
        setSelectedHorseId(null);
      }
    }
  }, [isOpen, horse]);

  const loadAnimaux = async () => {
    try {
      const animauxSnapshot = await getDocs(collection(db, 'animaux'));
      const animauxData = [];
      animauxSnapshot.forEach((doc) => {
        const data = doc.data();
        animauxData.push({
          id: doc.id,
          name: data.nom || '',
          sire: data.sire || '',
          type: data.type || 'equide'
        });
      });
      // Trier par nom
      animauxData.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setAllHorses(animauxData);
    } catch (error) {
      console.error('Erreur chargement animaux:', error);
    }
  };

  // Filtrer les suggestions d'animaux
  useEffect(() => {
    if (!horse && horseName.length > 0) {
      const filtered = allHorses.filter(animal => 
        animal.name && animal.name.toLowerCase().includes(horseName.toLowerCase())
      );
      setFilteredHorses(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setFilteredHorses([]);
      setShowSuggestions(false);
    }
  }, [horseName, horse, allHorses]);

  // Sélectionner un animal depuis les suggestions
  const selectHorse = (animal) => {
    setHorseName(animal.name);
    setHorseSire(animal.sire || '');
    setSelectedAnimalType(animal.type);
    setSelectedHorseId(animal.id);
    setShowSuggestions(false);
  };

  const getSubTypeLabel = (value) => {
    const labels = {
      'soins': 'Soins',
      'marechalerie': 'Maréchalerie',
      'dentisterie': 'Dentisterie',
      'autres': 'Autres'
    };
    return labels[value] || value;
  };

  const getSubTypeDescription = (value) => {
    const descriptions = {
      'soins': 'Vaccination, vermifuge, traitement, blessure...',
      'marechalerie': 'Ferrage, parage, soins des pieds...',
      'dentisterie': 'Soins dentaires, limage, extraction...',
      'autres': 'Ostéopathie, physiothérapie, autre intervention...'
    };
    return descriptions[value] || '';
  };

  const getPlaceholder = () => {
    const placeholders = {
      'soins': 'Ex: Vaccination annuelle, Vermifuge...',
      'marechalerie': 'Ex: Ferrage complet, Parage...',
      'dentisterie': 'Ex: Contrôle dentaire, Limage...',
      'autres': 'Ex: Séance ostéopathie, Physiothérapie...'
    };
    return placeholders[subType] || 'Ex: Intervention...';
  };

  const handleSubmit = async () => {
    // Validation
    if (!horse && !horseName.trim()) {
      alert('Veuillez sélectionner un animal');
      return;
    }
    
    if (!title.trim() || !description.trim()) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSaving(true);
    try {
      const now = new Date().toISOString();
      
      const newTicket = {
        type: 'medical',
        subType: subType,
        title: title.trim(),
        message: description.trim(),
        priority: priority,
        // Informations sur l'animal
        horseName: horse ? (horse.nom || 'Sans nom') : horseName.trim(),
        horseSire: horse ? (horse.sire || null) : (horseSire.trim() || null),
        horseId: horse ? horse.id : selectedHorseId,
        animalType: horse ? (horse.type || 'equide') : selectedAnimalType,
        // Métadonnées
        dateCreation: now,
        dateModification: now,
        acteur: 'Utilisateur',
        statut: 'ouvert',
        historique: [{
          date: now,
          acteur: 'Utilisateur',
          action: 'creation',
          message: `Création du ticket médical - ${getSubTypeLabel(subType)}`
        }]
      };
      
      await addDoc(collection(db, 'tickets'), newTicket);
      
      if (onSuccess) {
        onSuccess();
      }

      onClose();
      alert(`Ticket médical créé pour ${horse ? horse.nom : horseName}`);
      
    } catch (error) {
      console.error('Erreur lors de la création du ticket médical:', error);
      alert('Erreur lors de la création du ticket. Veuillez réessayer.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SimpleModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <FaHandHoldingMedical className="h-5 w-5 text-blue-600" />
          Nouveau ticket médical
        </div>
      }
    >
      <div className="space-y-6">
        {/* Information sur l'animal - Pré-rempli ou Recherche */}
        {horse ? (
          // Mode avec cheval pré-sélectionné
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              {horse.type === 'equide' ? (
                <GiHorseHead className="h-8 w-8 text-blue-600" />
              ) : (
                <PiFarm className="h-8 w-8 text-blue-600" />
              )}
              <div>
                <p className="font-semibold text-lg">{horse.nom || 'Sans nom'}</p>
                {horse.sire && (
                  <p className="text-sm text-gray-600">SIRE: {horse.sire}</p>
                )}
              </div>
            </div>
          </div>
        ) : (
          // Mode recherche d'animal
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Sélectionner l'animal *
            </label>
            <div className="relative">
              <div className="flex items-center">
                <Search className="absolute left-3 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={horseName}
                  onChange={(e) => {
                    setHorseName(e.target.value);
                    setShowSuggestions(true);
                    setSelectedHorseId(null);
                  }}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Rechercher un animal par son nom..."
                  autoComplete="off"
                />
              </div>
              
              {/* Suggestions d'animaux */}
              {showSuggestions && filteredHorses.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {filteredHorses.map((animal) => (
                    <div
                      key={animal.id}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectHorse(animal);
                      }}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        {animal.type === 'equide' ? (
                          <GiHorseHead className="h-4 w-4 text-gray-600" />
                        ) : (
                          <PiFarm className="h-4 w-4 text-gray-600" />
                        )}
                        <span className="font-medium">{animal.name}</span>
                      </div>
                      {animal.type === 'equide' && animal.sire && (
                        <span className="text-xs text-gray-500">SIRE: {animal.sire}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Champ SIRE optionnel */}
            {selectedAnimalType === 'equide' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SIRE (optionnel)
                </label>
                <input
                  type="text"
                  value={horseSire}
                  onChange={(e) => setHorseSire(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Numéro SIRE"
                />
              </div>
            )}
          </div>
        )}

        {/* Type d'intervention médicale */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Type d'intervention médicale *
          </label>
          <div className="space-y-2">
            {['soins', 'marechalerie', 'dentisterie', 'autres'].map((type) => (
              <label 
                key={type}
                className="flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50"
              >
                <input
                  type="radio"
                  name="medicalSubType"
                  value={type}
                  checked={subType === type}
                  onChange={(e) => setSubType(e.target.value)}
                  className="mr-3 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <span className="font-medium">{getSubTypeLabel(type)}</span>
                  <p className="text-xs text-gray-500 mt-1">
                    {getSubTypeDescription(type)}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Titre de l'intervention */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Titre de l'intervention *
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={getPlaceholder()}
            required
          />
        </div>

        {/* Priorité */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Priorité
          </label>
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => setPriority('basse')}
              className={`p-3 rounded-lg border-2 transition-all ${
                priority === 'basse' 
                  ? 'border-green-500 bg-green-50 text-green-700' 
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              }`}
            >
              <span className="font-medium">Basse</span>
              <p className="text-xs mt-1">Routine</p>
            </button>
            
            <button
              type="button"
              onClick={() => setPriority('moyenne')}
              className={`p-3 rounded-lg border-2 transition-all ${
                priority === 'moyenne' 
                  ? 'border-orange-500 bg-orange-50 text-orange-700' 
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              }`}
            >
              <span className="font-medium">Moyenne</span>
              <p className="text-xs mt-1">Important</p>
            </button>
            
            <button
              type="button"
              onClick={() => setPriority('haute')}
              className={`p-3 rounded-lg border-2 transition-all ${
                priority === 'haute' 
                  ? 'border-red-500 bg-red-50 text-red-700' 
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              }`}
            >
              <span className="font-medium">Haute</span>
              <p className="text-xs mt-1">Urgent</p>
            </button>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description de l'intervention *
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Décrivez l'intervention médicale nécessaire, les symptômes observés, le traitement prévu..."
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Ce ticket sera visible dans le tableau de bord et permettra de suivre l'intervention.
          </p>
        </div>

        {/* Message d'information selon la priorité */}
        <div 
          className={`p-3 rounded-lg border ${
            priority === 'haute' 
              ? 'bg-red-50 border-red-300 text-red-700' 
              : priority === 'moyenne'
              ? 'bg-orange-50 border-orange-300 text-orange-700'
              : 'bg-green-50 border-green-300 text-green-700'
          }`}
        >
          <p className="text-sm font-medium">
            {priority === 'haute' && "🚨 Priorité haute : Intervention urgente requise"}
            {priority === 'moyenne' && "⚠️ Priorité moyenne : À planifier rapidement"}
            {priority === 'basse' && "✓ Priorité basse : Intervention de routine"}
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={saving}
          >
            Annuler
          </button>
          <button 
            onClick={handleSubmit}
            disabled={saving || !title.trim() || !description.trim() || (!horse && !horseName.trim())}
            className={`px-4 py-2 rounded-md flex items-center gap-2 ${
              saving || !title.trim() || !description.trim() || (!horse && !horseName.trim())
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {saving ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                Création...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Créer le ticket médical
              </>
            )}
          </button>
        </div>
      </div>
    </SimpleModal>
  );
};

export default MedicalTicketModal;